<template>
  <div
    v-if="isLoading"
    class="relative h-full w-full"
    role="status"
    aria-live="polite"
    aria-label="Loading video">
    <div class="absolute inset-0 animate-pulse rounded bg-gray-200" />
  </div>

  <div
    v-show="!isLoading"
    class="relative mx-auto h-full w-full overflow-hidden">
    <!-- Poster overlay -->
    <button
      v-if="imagePath && showPoster"
      type="button"
      class="group absolute inset-0 z-20 flex cursor-pointer items-center justify-center bg-black transition-colors"
      :aria-label="isPlaying ? `Pause video: ${title}` : `Play video: ${title}`"
      :aria-controls="playerId"
      @click="toggleVideo">
      <div class="relative flex h-full w-full flex-col items-center justify-center overflow-hidden text-center">
        <img
          alt=""
          aria-hidden="true"
          role="presentation"
          :src="imagePath"
          class="absolute inset-0 -z-10 h-full w-full transform object-cover opacity-50 transition-transform duration-500 ease-out group-hover:scale-105" />

        <!-- YouTube play/pause button -->
        <div
          class="flex h-[60px] w-[60px] transform items-center justify-center rounded-full bg-black/60 backdrop-blur-sm transition-transform duration-500 ease-out hover:bg-black/70 group-hover:scale-105 sm:h-[70px] sm:w-[70px] md:h-[90px] md:w-[90px]">
          <!-- Play -->
          <svg
            v-if="!isPlaying"
            xmlns="http://www.w3.org/2000/svg"
            width="36"
            height="36"
            viewBox="0 0 24 24"
            fill="white"
            class="ml-[2px]">
            <path d="M8 5v14l11-7-11-7z" />
          </svg>

          <!-- Pause -->
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="white">
            <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
          </svg>
        </div>

        <!-- Title -->
        <h6
          class="mt-5 line-clamp-2 max-w-[85%] px-4 text-[14px] font-semibold leading-snug text-white sm:text-[16px] md:max-w-[70%] md:text-[22px] lg:text-[24px]">
          {{ title }}
        </h6>
      </div>
    </button>

    <!-- Pause button -->
    <button
      v-if="!showPoster && isPlaying"
      type="button"
      class="absolute right-3 top-3 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-black/70 hover:bg-black/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
      :aria-controls="playerId"
      :aria-label="`Pause video: ${title}`"
      @click="
        () => {
          try {
            player?.pauseVideo?.()
          } catch {}
        }
      ">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
        class="h-5 w-5 text-white">
        <path d="M6 5h4v14H6zM14 5h4v14h-4z" />
      </svg>
    </button>

    <div
      :id="playerId"
      class="relative z-10 h-full w-full" />
  </div>
</template>

<script setup lang="ts">
  import { onBeforeUnmount, onMounted, ref, shallowRef, useId, watch } from 'vue'

  import { createYouTubePlayer } from '~/composables/youtube'

  type Props = {
    imagePath?: string
    videoId: string
    title?: string
    index: number
    activeIndex?: number
  }

  const props = defineProps<Props>()
  const emit = defineEmits<{
    (e: 'play' | 'pause' | 'ended', index: number): void
  }>()

  // --- State ---
  const isLoading = ref(true)
  const isPlaying = ref(false)
  const showPoster = ref(true)
  const hasStarted = ref(false)
  const playerId = `youtube-player-${useId()}`
  const player = shallowRef<YT.Player | null>(null)

  // --- Stability check ---
  let stabilityTimer: ReturnType<typeof setInterval> | null = null
  let stabilityTimeout: ReturnType<typeof setTimeout> | null = null
  let lastSample = 0
  let stableCount = 0

  const clearStability = () => {
    if (stabilityTimer) {
      clearInterval(stabilityTimer)
      stabilityTimer = null
    }
    if (stabilityTimeout) {
      clearTimeout(stabilityTimeout)
      stabilityTimeout = null
    }
    stableCount = 0
  }

  const beginPauseStabilityCheck = () => {
    clearStability()
    stabilityTimeout = setTimeout(() => {
      try {
        lastSample = player.value?.getCurrentTime?.() ?? 0
      } catch {
        lastSample = 0
      }
      stableCount = 0

      stabilityTimer = setInterval(() => {
        try {
          const YTPS = window.YT.PlayerState
          const stateNow = player.value?.getPlayerState?.()
          const tNow = player.value?.getCurrentTime?.()

          if (stateNow !== YTPS.PAUSED) {
            clearStability()
            return
          }

          if (typeof tNow === 'number' && Math.abs(tNow - lastSample) < 0.05) {
            stableCount += 1
          } else {
            stableCount = 0
            if (typeof tNow === 'number') {
              lastSample = tNow
            }
          }

          if (stableCount >= 3) {
            showPoster.value = true
            clearStability()
          }
        } catch {
          clearStability()
        }
      }, 300)
    }, 300)
  }

  const toggleVideo = () => {
    if (!player.value) {
      return
    }
    player.value.playVideo()
    hasStarted.value = true
    showPoster.value = false
    isPlaying.value = true
    emit('play', props.index)
  }

  const onPlayerStateChange = (event: YT.OnStateChangeEvent) => {
    const YTPS = window.YT.PlayerState

    if (event.data === YTPS.PLAYING || event.data === YTPS.BUFFERING) {
      clearStability()
      hasStarted.value = true
      isPlaying.value = true
      showPoster.value = false
      emit('play', props.index)
    } else if (event.data === YTPS.PAUSED) {
      isPlaying.value = false
      emit('pause', props.index)

      if (!hasStarted.value) {
        showPoster.value = true
        return
      }
      beginPauseStabilityCheck()
    } else if (event.data === YTPS.ENDED) {
      clearStability()
      isPlaying.value = false
      showPoster.value = true
      hasStarted.value = false
      emit('ended', props.index)
    }
  }

  const handlePlayerReady = () => {
    isLoading.value = false
  }

  const initializePlayer = () => {
    createYouTubePlayer(playerId, props.videoId, handlePlayerReady, onPlayerStateChange)
      .then(loadedPlayer => {
        player.value = loadedPlayer
        try {
          const iframe: HTMLIFrameElement | undefined = player.value?.getIframe?.()
          if (iframe) {
            iframe.setAttribute('title', `YouTube video player: ${props.title}`)
          }
        } catch (error) {
          console.error(error)
        }
      })
      .catch(error => {
        console.error('Failed to initialize YouTube player:', error)
      })
  }

  onMounted(() => {
    initializePlayer()
  })

  onBeforeUnmount(() => {
    clearStability()
  })

  // Stop playback when not active
  watch(
    () => props.activeIndex,
    val => {
      if (val !== props.index && player.value?.stopVideo) {
        try {
          player.value.stopVideo()
        } catch (error) {
          console.error(error)
        }
        clearStability()
        isPlaying.value = false
        showPoster.value = true
        hasStarted.value = false
      }
    }
  )
</script>
